import { Component } from '../core/smpl-components.js'
import EmblaCarousel from 'embla-carousel'
import { ResizeCallback } from '../utils/resize-callback.js'

// -----------------------------------------------------------------------------
// Activities list.
//
// A row of activity cards. When the cards all fit, it stays a plain static row
// with no slider at all. When they overflow the viewport, we spin up an Embla
// slider and expose a prev/next toolbar (CSS restricts that toolbar to non-touch
// hover devices). Fit is re-measured on horizontal window resize, so Embla is
// created/destroyed as the breakpoint or width crosses the overflow boundary.
// Selector: `.js-activities-list` (see setup/components.js).
// -----------------------------------------------------------------------------
class ActivitiesSlider {
	constructor(options) {
		this.element = options.el

		this.elViewport = this.element.querySelector('.js-activities-slider')
		this.elInner = this.element.querySelector('.activities-list__inner')
		this.elPrev = this.element.querySelector('.js-activities-prev')
		this.elNext = this.element.querySelector('.js-activities-next')

		this.embla = null

		// Re-evaluate the fit whenever the window changes width.
		this.resizeCallback = new ResizeCallback(() => this.sync(), { detectAxis: ['x'] })
		this.sync()
	}

	// Do the cards overflow their viewport? 1px tolerance absorbs sub-pixel
	// rounding when a row fits exactly. scrollWidth is layout-based, so it stays
	// accurate even while Embla has the container translated.
	overflows() {
		return this.elInner.scrollWidth > this.elViewport.clientWidth + 1
	}

	sync() {
		if (this.overflows()) {
			this.initEmbla()
		} else {
			this.destroyEmbla()
		}
	}

	initEmbla() {
		if (this.embla) return

		this.embla = EmblaCarousel(this.elViewport, {
			align: 'start',
			containScroll: 'trimSnaps',
			skipSnaps: true,
			loop: false,
		})

		this.bndOnPrevClick = (e) => {
			e.preventDefault()
			this.embla.scrollPrev()
		}
		this.bndOnNextClick = (e) => {
			e.preventDefault()
			this.embla.scrollNext()
		}
		if (this.elPrev) this.elPrev.addEventListener('click', this.bndOnPrevClick)
		if (this.elNext) this.elNext.addEventListener('click', this.bndOnNextClick)

		this.bndUpdateArrows = this.updateArrows.bind(this)
		this.embla.on('select', this.bndUpdateArrows)

		this.element.classList.add('is-scrollable')
		this.updateArrows()
	}

	destroyEmbla() {
		if (!this.embla) return

		this.embla.off('select', this.bndUpdateArrows)
		this.embla.destroy()
		this.embla = null

		if (this.elPrev) this.elPrev.removeEventListener('click', this.bndOnPrevClick)
		if (this.elNext) this.elNext.removeEventListener('click', this.bndOnNextClick)

		this.element.classList.remove('is-scrollable')
	}

	updateArrows() {
		if (!this.embla) return
		if (this.elPrev) this.elPrev.classList.toggle('is-disabled', !this.embla.canScrollPrev())
		if (this.elNext) this.elNext.classList.toggle('is-disabled', !this.embla.canScrollNext())
	}

	destroy() {
		this.destroyEmbla()
		if (this.resizeCallback) {
			this.resizeCallback.destroy()
			this.resizeCallback = null
		}
	}
}

export default class extends Component {
	prepare() {
		this.slider = new ActivitiesSlider({ el: this.element })
	}

	mount() {}

	unmount() {
		if (this.slider) {
			this.slider.destroy()
			this.slider = null
		}
	}
}
